import React, { useMemo, useState, useEffect, useCallback } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import TanStackTable from '../../components/TanStackTable';
import { createColumnHelper } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { FaTrash, FaEdit } from 'react-icons/fa';
import { ActionButton } from '../../components/Forms/ActionButton';
import { useGetQuizzes, useDeleteQuiz } from '../../queries/quiz';
import Chip from '../../components/Chip';

const QuizList = () => {
  const navigate = useNavigate();
  const { data, isPending } = useGetQuizzes();
  const { mutateAsync: deleteQuiz, isPending: isDeleting } = useDeleteQuiz();
  const columnHelper = createColumnHelper();

  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    title: '',
    status: '',
    homepage: '',
    fromDate: '',
    toDate: '',
  });

  useEffect(() => {
    setFilteredData(data || []);
  }, [data]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    let filtered = data || [];

    if (filters.title) {
      filtered = filtered.filter((quiz) =>
        quiz.title?.toLowerCase().includes(filters.title.toLowerCase())
      );
    }

    if (filters.status) {
      const isActive = filters.status === 'active';
      filtered = filtered.filter((quiz) => quiz.active === isActive);
    }

    if (filters.homepage) {
      const isHomepage = filters.homepage === 'active';
      filtered = filtered.filter((quiz) => quiz.quizOfTheDay === isHomepage);
    }

    if (filters.fromDate) {
      const from = new Date(filters.fromDate);
      filtered = filtered.filter((quiz) => new Date(quiz.createdAt) >= from);
    }

    if (filters.toDate) {
      const to = new Date(filters.toDate);
      to.setHours(23, 59, 59, 999);
      filtered = filtered.filter((quiz) => new Date(quiz.createdAt) <= to);
    }

    setFilteredData(filtered);
  };

  const handleDelete = useCallback(async (id) => {
    if (isDeleting) return;
    const confirm = window.confirm("Are you sure you want to delete this quiz?");
    if (!confirm) return;

    try {
      await deleteQuiz(id);
      toast.success("Quiz deleted successfully");
    } catch (error) {
      toast.error("Failed to delete quiz: " + (error?.message || "Unknown error"));
    }
  }, [deleteQuiz, isDeleting]);

  const columns = useMemo(() => [
    columnHelper.display({
      id: 'srno',
      header: 'Sr No.',
      cell: (info) => info.row.index + 1,
      size: 80,
    }),
    columnHelper.accessor('title', {
      header: 'Title',
      cell: (info) => info.getValue() || 'N/A',
      size: 200,
    }),
    columnHelper.accessor('createdAt', {
      header: 'Created At',
      align: 'center',
      size: 160,
      cell: (info) => {
        const value = info.getValue();
        if (!value) return 'N/A';
        const date = new Date(value);
        return (
          <div className='text-center'>
            {date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
            <br />
            <small className='text-secondary'>
              {date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </small>
          </div>
        );
      },
    }),
    columnHelper.accessor('pointsPerCorrectAnswer', {
      header: 'Points Per Question',
      align: 'center',
      size: 140,
      cell: (info) => <div className='text-center'>{info.getValue() || 0}</div>,
    }),
    columnHelper.accessor((row) => {
      const pointsPerQuestion = row.pointsPerCorrectAnswer || 0;
      const numberOfQuestions = row.questionIds?.length || 0;
      return pointsPerQuestion * numberOfQuestions;
    }, {
      id: 'totalPoints',
      header: 'Total Points',
      align: 'center',
      size: 120,
      cell: (info) => <div className='text-center'>{info.getValue()}</div>,
    }),
    columnHelper.accessor('active', {
      header: 'Status',
      align: 'center',
      size: 120,
      cell: (info) => <Chip value={info.getValue() ? 'Active' : 'Inactive'} color={info.getValue() ? 'green' : 'inactive'} />,
    }),
    columnHelper.accessor('quizOfTheDay', {
      header: 'Homepage',
      align: 'center',
      size: 120,
      cell: (info) => <Chip value={info.getValue() ? 'Yes' : 'No'} color={info.getValue() ? 'green' : 'inactive'} />,
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      align: 'center',
      size: 150,
      enableColumnFilter: false,
      cell: (info) => (
        <div className='flex items-center justify-center'>
          <button
            className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
            onClick={() => navigate('/quiz/create-quiz', { state: { quizId: info.row.original?._id } })}
          >
            <FaEdit className="text-chipPrimary" />
          </button>
          <button
            className="rounded p-1.5 bg-[rgba(199,52,52,0.1)] hover:bg-[rgba(199,52,52,0.2)] mx-1 min-h-7"
            onClick={() => handleDelete(info.row.original._id)}
          >
            <FaTrash className="fill-chipRed" />
          </button>
        </div>
      ),
    }),
  ], [columnHelper, handleDelete, navigate]);

  if (isPending) {
    return (
      <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
        <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
          <div className="flex flex-col pr-px pl-6 w-full max-md:pl-5 max-md:max-w-full">
            <h1 className="self-start text-lg font-semibold">List of Quizzes</h1>
            <p>Loading...</p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
      <form
        className="flex flex-col pt-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)]"
        onSubmit={handleSearch}
      >
        <div className='flex items-center justify-between pr-5'>
          <div className="flex gap-1.5 items-start my-auto text-base whitespace-nowrap ml-5">
            <img
              loading="lazy"
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/4dd39feef0ff4934d9e2e86b8c7285d5d7532aeb26adcdc1e878d021439e2681?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
              alt=""
              className="object-contain shrink-0 w-5 aspect-square"
            />
            <span className="self-stretch">Filters</span>
            <img
              loading="lazy"
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/d18f51a0b2d597a302f55b77fc1071043eab5f274a1cc9e9b3fe3e2c97520a8c?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
              alt=""
              className="object-contain shrink-0 aspect-square w-[18px]"
            />
          </div>
          <ActionButton
            variant={'reset'}
            text={'Create New Quiz'}
            handleSubmit={() => navigate('/quiz/create-quiz')}
          />
        </div>
        <div className="flex flex-wrap gap-4 p-5">
          <div className='w-full md:w-1/3'>
            <label htmlFor="title" className="text-gray-900 text-base">
              Quiz Title
            </label>
            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full bg-white rounded-md border border-navBtn text-slate-400">
              <input
                type="text"
                name="title"
                value={filters.title}
                onChange={handleFilterChange}
                placeholder="Enter Quiz Title"
                className='outline-none focus:ring-0 w-full'
              />
            </div>
          </div>
          <div className='w-full md:w-1/3'>
            <label htmlFor="status" className="text-gray-900 text-base">
              Status
            </label>
            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full bg-white rounded-md border border-navBtn text-slate-400">
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="flex-1 bg-transparent border-none outline-none text-base"
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div className='w-full md:w-1/3'>
            <label htmlFor="homepage" className="text-gray-900 text-base">
              Homepage
            </label>
            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full bg-white rounded-md border border-navBtn text-slate-400">
              <select
                name="homepage"
                value={filters.homepage}
                onChange={handleFilterChange}
                className="flex-1 bg-transparent border-none outline-none text-base"
              >
                <option value="">All</option>
                <option value="active">Quiz of the Day</option>
                <option value="inactive">Not Quiz of the Day</option>
              </select>
            </div>
          </div>
          <div className='w-full md:w-1/3'>
            <label htmlFor="fromDate" className="text-gray-900 text-base">
              From Date
            </label>
            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full bg-white rounded-md border border-navBtn text-slate-400">
              <input
                type="date"
                name="fromDate"
                value={filters.fromDate}
                onChange={handleFilterChange}
                className="w-full outline-none"
              />
            </div>
          </div>
          <div className='w-full md:w-1/3'>
            <label htmlFor="toDate" className="text-gray-900 text-base">
              To Date
            </label>
            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full bg-white rounded-md border border-navBtn text-slate-400">
              <input
                type="date"
                name="toDate"
                value={filters.toDate}
                onChange={handleFilterChange}
                className="w-full outline-none"
              />
            </div>
          </div>
        </div>
        <div className='flex gap-3 items-center justify-end p-5'>
          <ActionButton
            variant={'reset'}
            text={'Reset'}
            handleSubmit={() => {
              setFilters({
                title: '',
                status: '',
                homepage: '',
                fromDate: '',
                toDate: '',
              });
              setFilteredData(data || []);
            }}
          />
          <ActionButton
            variant={'submit'}
            text={'Search'}
            handleSubmit={handleSearch}
          />
        </div>
      </form>

      <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
        <div className="flex flex-col pr-px w-full pl-6 max-md:max-w-full">
          <h1 className="self-start text-lg font-bold pb-4">List of Quizzes</h1>
          {filteredData && filteredData.length > 0 ? (
            <TanStackTable
              data={filteredData}
              columns={columns}
              enablePagination={true}
            />
          ) : (
            <div className="pb-4">
              No quizzes available
            </div>
          )}
        </div>
      </section>
      <ToastContainer
        position="top-right"
        autoClose={3000}
      />
    </main>
  );
};

export default QuizList;
